'use client'

import { Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import {
	AlertDialog,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { useDeleteTaxRates } from '@/lib/api/taxRates'
import { useToast } from '@/components/ui/use-toast'

interface BulkDeleteTaxRatesModalProps {
	taxRateIds: string[]
	onClose: () => void
	onSuccess?: () => void
}

/**
 * Modal component that confirms bulk tax rate deletion
 */
export const BulkDeleteTaxRatesModal = ({ taxRateIds, onClose, onSuccess }: BulkDeleteTaxRatesModalProps) => {
	const { t } = useTranslation('taxes')
	const { t: tCommon } = useTranslation('common')
	const { toast } = useToast()
	const deleteTaxRates = useDeleteTaxRates()

	const count = taxRateIds.length

	const handleDelete = async () => {
		if (taxRateIds.length === 0) return

		try {
			await deleteTaxRates.mutateAsync({ ids: taxRateIds })

			toast({
				title: tCommon('success'),
				description: t('deleteBulkSuccess', { count }),
			})

			onSuccess?.()
			onClose()
		} catch (error) {
			toast({
				title: tCommon('error'),
				description: t('deleteBulkError'),
				variant: 'destructive',
			})
		}
	}

	return (
		<AlertDialog open={true} onOpenChange={onClose}>
			<AlertDialogContent>
				<AlertDialogHeader>
					<AlertDialogTitle>{t('modals.bulkDelete.title')}</AlertDialogTitle>
					<AlertDialogDescription>
						{t('modals.bulkDelete.description', { count })}
					</AlertDialogDescription>
				</AlertDialogHeader>
				<AlertDialogFooter>
					<Button variant="outline" onClick={onClose} disabled={deleteTaxRates.isPending}>
						{tCommon('cancel')}
					</Button>
					<Button variant="destructive" onClick={handleDelete} disabled={deleteTaxRates.isPending}>
						{deleteTaxRates.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
						{tCommon('delete')}
					</Button>
				</AlertDialogFooter>
			</AlertDialogContent>
		</AlertDialog>
	)
}

